/**
 * 約定ティッカーの配列 (呼び出し側が新しい順に並べたもの) を、先頭から 100 件ずつ描画する hook (#9581)。
 *
 * `resetKey` (受渡日・コマ) が変わると先頭の 100 件に戻す。
 */
import { type UIEvent, useCallback, useMemo, useState } from 'react'

import {
  MARKET_TICKER_PAGE_SIZE,
  isNearScrollEnd,
  nextTickerVisibleCount,
} from '~/pages/IntradayBidding/models/marketTickerWindow'

export function useMarketTickerWindow<T>(
  items: readonly T[],
  resetKey: string,
): {
  visibleItems: readonly T[]
  /** スクロール領域の `onScroll` に渡す。 下端に近づいたら次の 100 件を足す。 */
  onScroll: (event: UIEvent<HTMLElement>) => void
} {
  const [state, setState] = useState({
    resetKey,
    count: MARKET_TICKER_PAGE_SIZE,
  })
  // resetKey が変わった描画の中で件数を書き戻す (React が直ちに再描画する)。
  // 描画の外で戻すと、 A → B → A と戻った時に A の増やした件数が復活する。
  if (state.resetKey !== resetKey) {
    setState({ resetKey, count: MARKET_TICKER_PAGE_SIZE })
  }
  const visibleCount = state.count

  const visibleItems = useMemo(
    () => items.slice(0, visibleCount),
    [items, visibleCount],
  )

  const total = items.length
  const onScroll = useCallback(
    (event: UIEvent<HTMLElement>) => {
      if (visibleCount >= total || !isNearScrollEnd(event.currentTarget)) {
        return
      }
      // 描画前に scroll event が続けて来ても同じ値になるよう、 現在の件数から算出する。
      setState({ resetKey, count: nextTickerVisibleCount(visibleCount, total) })
    },
    [resetKey, total, visibleCount],
  )

  return { visibleItems, onScroll }
}
